/**
 * Controls the full resolution flow after two cards are flipped:
 * - Uses resolveFlippedCards (pure logic)
 * - Sets matched or mismatched state
 * - Applies scoring logic (via updatePlayerScore)
 * - Delays flipping back unmatched cards
 * - Triggers sound/animation feedback via handlers
 * - Calls onMatch or onMismatch accordingly
 */
import { resolveFlippedCards } from '../logic/resolveFlippedCards'
import { FLIP_BACK_DELAY } from '../constants/memorama'
import { VICTORY_SOUND_DELAY, END_SCREEN_TRANSITION_DELAY } from '@shared'
import { handleMatchOutcome } from './handleMatchOutcome'
import { handleMismatchOutcome } from './handleMismatchOutcome'
import { updatePlayerScore } from '../logic/updatePlayerScore'
import { checkEndGame } from '../logic/checkEndGame'
import { handlePlaySound } from '@handlers/handlePlaySound'

export function handleFlipResolution ({
  flippedCards,
  cards,
  setCards,
  setFlippedCards,
  onMatch,
  onMismatch,
  unlockBoard,
  nextTurn,
  players,
  currentTurnIndex,
  setPlayers,
  incrementTurn,
  setIsGameOver,
  registerTimer
}) {
  const isMatch = resolveFlippedCards(flippedCards)

  if (isMatch) {
    const matchedImage = flippedCards[0].image
    const updatedCards = cards.map(card =>
      card.image === matchedImage
        ? { ...card, matched: true, justMatched: true }
        : card
    )

    setCards(updatedCards)
    setFlippedCards([])

    const updatedPlayers = updatePlayerScore({
      players,
      currentTurnIndex,
      matchedImage,
      result: 'match'
    })

    setPlayers(updatedPlayers)
    handleMatchOutcome()

    // 🛡️ DEFENSIVE: Register timer for cleanup
    const matchCleanupTimer = setTimeout(() => {
      setCards(prev => prev.map(card =>
        card.justMatched
          ? { ...card, justMatched: false }
          : card
      ))
    }, 700)
    registerTimer(matchCleanupTimer)

    onMatch(matchedImage, updatedCards)

    if (checkEndGame(updatedCards)) {
      // 🛡️ DEFENSIVE: Register end game timers
      const endSoundTimer = setTimeout(() => {
        handlePlaySound('end')
      }, VICTORY_SOUND_DELAY)
      registerTimer(endSoundTimer)

      incrementTurn('endGame')

      if (import.meta.env.MODE === 'development') {
        console.log('[🏁 GAME END - FINAL TURN]', {
          player: players[currentTurnIndex].name,
          finalMatch: true,
          endGameTriggered: true
        })
      }

      const endGameTimer = setTimeout(() => setIsGameOver(true), END_SCREEN_TRANSITION_DELAY)
      registerTimer(endGameTimer)
    }
    unlockBoard()
  } else {
    const mismatchedIds = flippedCards.map(fc => fc.id)

    const updatedCards = cards.map(card =>
      mismatchedIds.includes(card.id)
        ? { ...card, justMismatched: true }
        : card
    )

    setCards(updatedCards)
    handleMismatchOutcome()
    onMismatch()

    const updatedPlayers = updatePlayerScore({
      players,
      currentTurnIndex,
      result: 'mismatch'
    })

    setPlayers(updatedPlayers)
    if (import.meta.env.MODE === 'development') {
      console.log('❌ MISMATCH - TURN ENDING:', {
        player: players[currentTurnIndex].name,
        aboutToIncrementTurn: true
      })
    }

    // 🛡️ DEFENSIVE: Register mismatch timers for cleanup
    const mismatchTimer = setTimeout(() => {
      const revertedCards = updatedCards.map(card =>
        mismatchedIds.includes(card.id)
          ? { ...card, flipped: false, justMismatched: false }
          : card
      )

      setCards(revertedCards)
      setFlippedCards([])
      unlockBoard()
      incrementTurn('mismatch', updatedPlayers)
      nextTurn()
    }, FLIP_BACK_DELAY)
    registerTimer(mismatchTimer)
  }
}
